import React, { useMemo } from 'react';
import { AlertCircle, AlertTriangle, Pin } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Tooltip } from '../ui/tooltip';
import type { BoardModel } from './boardModel';
import {
  ABSENCE_APPROVED_CLASSES,
  ABSENCE_PENDING_CLASSES,
  ExceptionMark,
  HEADCOUNT_BELOW_MIN_CLASSES,
  HEADCOUNT_BELOW_TARGET_CLASSES,
  HEADCOUNT_OK_CLASSES,
} from './PlanBoard';
import {
  DAY_OFF_CLASSES,
  SlotTimeOfDay,
  TIME_OF_DAY_LABELS,
  TIME_OF_DAY_ORDER,
  slotHours,
  slotTimeKey,
  slotTimeOfDay,
  timeOfDayClass,
} from './planningUtils';

/**
 * Explains the symbols used on the board. Shift chips are grouped by time of
 * day (only the groups someone can work are listed); the exact hours of each
 * group are in the tooltip.
 */
export const PlanLegend: React.FC<{ model: BoardModel; usedSlotIds: Set<string> }> = ({ model, usedSlotIds }) => {
  const groups = useMemo(() => {
    const byTod = new Map<SlotTimeOfDay, Map<string, string>>();
    usedSlotIds.forEach((id) => {
      const s = model.slotById.get(id);
      if (!s) return;
      const tod = slotTimeOfDay(s.startTime);
      const hours = byTod.get(tod) ?? new Map<string, string>();
      hours.set(slotTimeKey(s.startTime, s.endTime), slotHours(s.startTime, s.endTime));
      byTod.set(tod, hours);
    });
    return TIME_OF_DAY_ORDER.filter((tod) => byTod.has(tod)).map((tod) => ({
      tod,
      hours: Array.from(byTod.get(tod)!.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([, label]) => label),
    }));
  }, [model, usedSlotIds]);

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground" data-testid="plan-legend">
      {groups.map(({ tod, hours }) => (
        <Tooltip
          key={tod}
          side="top"
          content={
            <div className="max-w-xs whitespace-normal">
              Zmiana {TIME_OF_DAY_LABELS[tod].label.toLocaleLowerCase('pl')} — {TIME_OF_DAY_LABELS[tod].hint}.
              Godziny w tym tygodniu: {hours.join(', ')}.
            </div>
          }
        >
          <span className="inline-flex cursor-help items-center gap-1" data-testid="plan-legend-tod">
            <span className={cn('rounded border px-1.5 py-0.5 font-semibold', timeOfDayClass(tod))}>
              {TIME_OF_DAY_LABELS[tod].label}
            </span>
          </span>
        </Tooltip>
      ))}
      <span className="inline-flex items-center gap-1">
        <span className={cn('rounded border px-1.5 py-0.5 font-medium', DAY_OFF_CLASSES)}>W</span> dzień wolny
      </span>
      <span className="inline-flex items-center gap-1">
        <span className={cn('rounded px-1', ABSENCE_APPROVED_CLASSES)}>Urlop</span>
        <span className={cn('rounded px-1', ABSENCE_PENDING_CLASSES)}>Urlop?</span>
        nieobecność / wniosek czeka
      </span>
      <span className="inline-flex items-center gap-1">
        <Pin className="w-3 h-3" /> przypięte
      </span>
      <span className="inline-flex items-center gap-1 font-medium text-foreground">
        <ExceptionMark /> wyjątek
      </span>
      <span className="inline-flex items-center gap-1 font-medium text-foreground">
        <AlertCircle className="w-3.5 h-3.5 text-red-600 dark:text-red-400" /> błąd
      </span>
      <span className="inline-flex items-center gap-1 font-medium text-foreground">
        <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" /> ostrzeżenie
      </span>
      <span className="inline-flex items-center gap-1">
        obsada (zaplanowano/docelowo):
        <span className={cn('rounded px-1 tabular-nums', HEADCOUNT_OK_CLASSES)}>3/3</span>
        <span className={cn('rounded px-1 tabular-nums', HEADCOUNT_BELOW_TARGET_CLASSES)}>2/3</span>
        poniżej celu
        <span className={cn('rounded px-1 tabular-nums', HEADCOUNT_BELOW_MIN_CLASSES)}>1/3</span>
        poniżej minimum
      </span>
    </div>
  );
};
